/**
 * The byte-file seam the Journal repository is built on.
 *
 * F-JP-18 through F-JP-27 are a *per-record file* contract: identity is the
 * filename, a preserved record is a file that was copied rather than rewritten,
 * a deletion is a staging bundle behind a durable manifest, and a write is a
 * backup plus two verification reads. None of that is expressible against the
 * record store in `../kernel`, which addresses decoded rows in typed object
 * stores and has no notion of a path, a directory, or bytes that will not
 * decode staying exactly where they are.
 *
 * So the repository is written against this narrow interface instead, and the
 * whole transactional contract — backup, verify, rollback, bundle, recover —
 * lives above it in code that never touches a platform. `@ff/store` may not call
 * a browser API (`03-ENGINEERING-SEAMS.md` §1.2), so the substrate arrives as a
 * constructor argument: {@link MemoryJournalFileStore} in tests, an OPFS-backed
 * adapter from the platform layer in the browser.
 *
 * Two shapes here are deliberate rather than convenient:
 *
 *  1. **A read is a three-way union, never `Uint8Array | undefined`.** A file
 *     that is absent and a file the substrate could not read are different
 *     facts: the first is an empty Journal, the second is a Journal whose bytes
 *     must not be replaced by one. Collapsing them is the exact failure §3.6 of
 *     the architecture exists to prevent, one layer lower down.
 *  2. **Everything else throws {@link JournalFileError} carrying a repository
 *     error code.** The alternative — a result union on every operation — makes
 *     the twelve-step verified write in `verifiedWrite.ts` unreadable, and the
 *     one thing that write must never do is take a wrong branch.
 */

import type { JournalRepositoryErrorCode } from "@ff/domain";

/**
 * A substrate failure, already carrying the code the repository will report.
 *
 * The code is chosen at the point the failure happens rather than mapped later:
 * only the operation knows whether it was a directory that would not enumerate
 * (`enumerationFailed`) or a file that would not be written (`ioFailure`), and a
 * caller that has to guess will guess the same thing for both.
 */
export class JournalFileError extends Error {
  readonly code: JournalRepositoryErrorCode;
  readonly path: string;

  constructor(code: JournalRepositoryErrorCode, path: string, message: string) {
    super(`${message} (${path})`);
    this.name = "JournalFileError";
    this.code = code;
    this.path = path;
  }
}

/**
 * The outcome of reading one file.
 *
 * `failed` carries a detail rather than an error code because the caller is
 * always the decode pipeline, which turns an unreadable *file* into
 * `ioFailure` and an unreadable *record* into a quarantine item — two very
 * different conclusions from bytes that both "did not come back".
 */
export type JournalFileRead =
  | { readonly kind: "bytes"; readonly bytes: Uint8Array }
  | { readonly kind: "missing" }
  | { readonly kind: "failed"; readonly detail: string };

/**
 * A flat, path-addressed store of byte files.
 *
 * Paths are POSIX-style and relative to the Journal root (`JournalData`), which
 * is why nothing here takes an absolute location: the approved-root check in
 * `layout.ts` is a string comparison against a relative prefix, and an absolute
 * path would make that check a substrate-specific parse.
 */
export interface JournalFileStore {
  /** Creates the directory and every missing parent. Idempotent. */
  createDirectory(path: string): Promise<void>;

  /** Whether the path resolves to a directory right now. */
  directoryExists(path: string): Promise<boolean>;

  /**
   * Direct child names, files and directories alike, in an unspecified order.
   * Throws `enumerationFailed` when the directory resolves but will not list —
   * the fail-closed trigger F-JP-19 names.
   */
  list(directory: string): Promise<readonly string[]>;

  /** Whether the path resolves to a file right now. */
  fileExists(path: string): Promise<boolean>;

  read(path: string): Promise<JournalFileRead>;

  /** Creates missing parents, then replaces the file's contents. */
  write(path: string, bytes: Uint8Array): Promise<void>;

  /** Removes one file. Removing a file that is not there is not an error. */
  remove(path: string): Promise<void>;

  /** Removes a directory and everything under it. Absent is not an error. */
  removeDirectory(path: string): Promise<void>;

  /**
   * Relocates one file. The destination is replaced if it exists, so callers
   * that must not overwrite check first — quarantine is exactly such a caller
   * (F-JP-23), and giving it a "move that clobbers" is how a preserved record
   * gets destroyed by the second copy of itself.
   */
  move(from: string, to: string): Promise<void>;
}

/** Joins path segments, skipping empty ones so a root of `""` stays valid. */
export function journalPathJoin(...segments: readonly string[]): string {
  return segments.filter((segment) => segment.length > 0).join("/");
}

/** The last path segment, or the whole path when there is no separator. */
export function journalPathName(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut === -1 ? path : path.slice(cut + 1);
}

/** Everything before the last separator; `""` for a top-level path. */
export function journalPathParent(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut === -1 ? "" : path.slice(0, cut);
}

/**
 * Byte equality, used everywhere a write or a move is verified.
 *
 * Length first, because the common failure — a truncated write — is settled
 * without walking either buffer.
 */
export function journalBytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  for (let index = 0; index < left.byteLength; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}
